import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const CONSENT_KEY = 'aif_cookie_consent';

export default function CookieConsent({ token }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Show banner after short delay
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = async (acceptAll) => {
    const consent = {
      essential: true, // always required
      analytics: acceptAll ? true : analytics,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);

    // Send consent to backend if authenticated
    if (token) {
      const types = [
        { consent_type: 'cookies_essential', granted: true },
        { consent_type: 'cookies_analytics', granted: consent.analytics },
      ];
      for (const c of types) {
        try {
          await apiClient.post('/api/v1/aif/gdpr/consent', c, { queueOnOffline: true });
        } catch { /* ApiError — non-critical, consent saved locally */ }
      }
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[var(--aif-z-sticky)] p-4">
      <div className="max-w-2xl mx-auto bg-[var(--aif-bg-elevated)] border border-[var(--aif-border)] rounded-[var(--aif-radius-lg)] shadow-[var(--aif-shadow-lg)] p-5">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[var(--aif-text-primary)]">
            {t('cookies.message')}
          </p>

          {showDetails && (
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2 text-[var(--aif-text-secondary)]">
                <input type="checkbox" checked disabled className="accent-[var(--aif-color-primary)]" />
                {t('cookies.essential')}
              </label>
              <label className="flex items-center gap-2 text-[var(--aif-text-secondary)]">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="accent-[var(--aif-color-primary)]"
                />
                {t('cookies.analytics')}
              </label>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => saveConsent(true)}
              className="px-4 py-2 text-sm font-medium rounded-[var(--aif-radius-md)] bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition"
            >
              {t('cookies.acceptAll')}
            </button>
            {!showDetails ? (
              <button
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 text-sm font-medium rounded-[var(--aif-radius-md)] border border-[var(--aif-border)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] transition"
              >
                {t('cookies.settings')}
              </button>
            ) : (
              <button
                onClick={() => saveConsent(false)}
                className="px-4 py-2 text-sm font-medium rounded-[var(--aif-radius-md)] border border-[var(--aif-border)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] transition"
              >
                {t('cookies.saveSelection')}
              </button>
            )}
            <button
              onClick={() => saveConsent(false)}
              className="px-4 py-2 text-sm font-medium text-[var(--aif-text-muted)] hover:text-[var(--aif-text-secondary)] transition"
            >
              {t('cookies.essentialOnly')}
            </button>
          </div>

          <a
            href="/datenschutz"
            className="text-xs text-[var(--aif-text-muted)] hover:text-[var(--aif-color-primary)] underline transition"
          >
            {t('cookies.privacyLink')}
          </a>
        </div>
      </div>
    </div>
  );
}
